import type { DisplayZone } from "@/shared/datetime";

/** A span of time in milliseconds since the epoch. */
export interface TimeWindow {
  start: number;
  end: number;
}

export type TimeZoneMode = "local" | "utc";

/**
 * A new window, or a function of the current one. Returning `null` from the
 * function leaves the window as it is.
 */
export type WindowUpdate =
  TimeWindow | ((previous: TimeWindow) => TimeWindow | null);

/** Whether two windows have the same edges. */
export function sameWindow(a: TimeWindow, b: TimeWindow): boolean {
  return a.start === b.start && a.end === b.end;
}

/** The zone a formatter is resolved in for a Local/UTC choice: none for local. */
export function displayZone(tz: TimeZoneMode): DisplayZone | undefined {
  return tz === "utc" ? "UTC" : undefined;
}

export const SECOND = 1_000;
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

/** The narrowest window the user can zoom to. */
export const MIN_SPAN = 5 * SECOND;

/** How much of the extent the view opens on when there is no remembered window. */
export const DEFAULT_SPAN = 30 * DAY;

/** Where `time` falls in `window`, as a fraction of its span (outside 0..1 when off screen). */
export function fractionOf(window: TimeWindow, time: number): number {
  return (time - window.start) / spanOf(window);
}

export function spanOf(window: TimeWindow): number {
  return window.end - window.start;
}

/**
 * Keep `window` inside `extent`, at least `MIN_SPAN` wide, and never wider
 * than the extent. A window wider than the extent collapses to the extent; a
 * window that pokes out of one side slides back in without changing its span.
 */
export function clampWindow(
  window: TimeWindow,
  extent: TimeWindow,
): TimeWindow {
  const extentSpan = Math.max(spanOf(extent), MIN_SPAN);
  const span = Math.min(Math.max(spanOf(window), MIN_SPAN), extentSpan);
  const start = Math.min(
    Math.max(window.start, extent.start),
    extent.end - span,
  );
  return { start, end: start + span };
}

/**
 * The window the view opens on: the last `DEFAULT_SPAN` of the extent, or the
 * whole extent when it is shorter (R-8). A remembered `initial` window wins
 * when given, still clamped.
 */
export function initialWindow(
  extent: TimeWindow,
  initial?: TimeWindow | null,
): TimeWindow {
  if (initial) {
    return clampWindow(initial, extent);
  }
  return clampWindow(
    { start: extent.end - DEFAULT_SPAN, end: extent.end },
    extent,
  );
}

/**
 * Scale the window's span by `factor` around `anchor` (a time inside the
 * window; defaults to its center), so the point under the cursor stays put.
 * `factor < 1` zooms in.
 */
export function zoomWindow(
  window: TimeWindow,
  factor: number,
  anchor: number = (window.start + window.end) / 2,
): TimeWindow {
  return {
    start: anchor - (anchor - window.start) * factor,
    end: anchor + (window.end - anchor) * factor,
  };
}

/** Slide the window by `deltaMs` without changing its span. */
export function panWindow(window: TimeWindow, deltaMs: number): TimeWindow {
  return { start: window.start + deltaMs, end: window.end + deltaMs };
}

/** Slide the window so it is centered on `ms`, without changing its span. */
export function centerWindow(window: TimeWindow, ms: number): TimeWindow {
  const half = spanOf(window) / 2;
  return { start: ms - half, end: ms + half };
}

/** A window of `span` milliseconds centered where `window` is centered. */
export function resizeWindow(window: TimeWindow, span: number): TimeWindow {
  const center = (window.start + window.end) / 2;
  return { start: center - span / 2, end: center + span / 2 };
}

/**
 * The window that fits every item in `[start, end]` with a little padding,
 * never narrower than ten seconds so a single instant still shows as a span.
 */
export function fitWindow(start: number, end: number): TimeWindow {
  const span = Math.max(end - start, 10 * SECOND);
  const pad = span * 0.06;
  const center = (start + end) / 2;
  return { start: center - span / 2 - pad, end: center + span / 2 + pad };
}
